import { firebaseAuth } from "@/shared/integrations/firebase/client";

export type UserTeamMembership = {
  /** Firebase team ids the user belongs to (member + admin). */
  teamRefIds: string[];
  /** When true, the user is associated with ALL teams in their org. */
  autoSyncTeams: boolean;
  /** Org owner/admin — sees the whole org regardless of team membership. */
  isOrgOwner: boolean;
};

const FIREBASE_PROJECT_ID =
  (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined) ||
  "disco-outpost-268223";

// Short-lived cache: a single render burst resolves the scope several times
// (count + page + stage counts). Keyed by uid so it resets on user change.
let _cache: { at: number; uid: string; promise: Promise<UserTeamMembership> } | null =
  null;
const CACHE_TTL_MS = 30_000;

/** Pull a string[] out of a Firestore REST arrayValue field. */
function readStringArray(field: unknown): string[] {
  const values = (field as { arrayValue?: { values?: unknown[] } })?.arrayValue
    ?.values;
  return (values ?? [])
    .map((v) => (v as { stringValue?: string })?.stringValue)
    .filter((s): s is string => Boolean(s));
}

/**
 * The teams the signed-in user can access, read from their Firebase user doc via
 * the Firestore REST API (the site-manager ships no Firestore SDK; the Firebase
 * ID token is the Bearer credential, and Firestore rules still apply).
 *
 * Used to scope property queries by team rather than by an ~org-sized id list.
 * Degrades to "no teams" (empty) on any error so callers fail closed.
 */
export async function getUserTeamMembership(): Promise<UserTeamMembership> {
  const user = firebaseAuth?.currentUser;
  if (!user) return { teamRefIds: [], autoSyncTeams: false, isOrgOwner: false };

  const now = Date.now();
  if (_cache && _cache.uid === user.uid && now - _cache.at < CACHE_TTL_MS) {
    return _cache.promise;
  }

  const promise = (async (): Promise<UserTeamMembership> => {
    try {
      const token = await user.getIdToken();
      const url = `https://firestore.googleapis.com/v1/projects/${FIREBASE_PROJECT_ID}/databases/(default)/documents/users/${user.uid}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.warn(`getUserTeamMembership: ${res.status} reading user doc`);
        return { teamRefIds: [], autoSyncTeams: false, isOrgOwner: false };
      }
      const fields = ((await res.json()) as { fields?: Record<string, unknown> })
        .fields ?? {};
      const teamRefIds = [
        ...new Set([
          ...readStringArray(fields.teamIds),
          ...readStringArray(fields.teamAdminIds),
        ]),
      ];
      const autoSyncTeams =
        (fields.autoSyncTeams as { booleanValue?: boolean })?.booleanValue ===
        true;
      const isOrgOwner =
        (fields.isOrgOwner as { booleanValue?: boolean })?.booleanValue === true;
      return { teamRefIds, autoSyncTeams, isOrgOwner };
    } catch (e) {
      console.warn("getUserTeamMembership: failed", e);
      return { teamRefIds: [], autoSyncTeams: false, isOrgOwner: false };
    }
  })();

  _cache = { at: now, uid: user.uid, promise };
  return promise;
}
